import React from "react";
import Select from "./Select";
import SearchAndInsert from "./SearchAndInsert";
import TrialBanner from "./TrialBanner";
import PreAuthWelcome from "./PreAuthWelcome";
import GuidanceBanner from "./GuidanceBanner";
import AccountSettings from "./AccountSettings";
import Header from "./Header";
import PaywallModal from "./PaywallModal";
import {
	Button,
	makeStyles,
	tokens,
	Text,
	Spinner,
} from "@fluentui/react-components";
import { useClasses } from "../hooks/useClasses";
import { useSubjects } from "../hooks/useSubjects";
import { useSkillsAndConcepts } from "../hooks/useSkillsAndConcepts";
import { useStrandData } from "../hooks/useStrandData";
import { useStrandUnits } from "../hooks/useStrandUnits";
import { useLearningOutcomes } from "../hooks/useLearningOutcomes";
import { useOnboardingGuidance } from "../hooks/useOnboardingGuidance";
import { useAuth } from "../context/BaseAuthProvider";
import { useSubscription } from "../context/SubscriptionContext";

/**
 * Strip HTML tags from a string and capitalize the first letter.
 */
function cleanText(html: string): string {
	const text = html.replace(/<[^>]*>/g, "").trim();
	if (!text) return text;
	return text.charAt(0).toUpperCase() + text.slice(1);
}

type Props = {};

const useStyles = makeStyles({
	appRoot: {
		display: "flex",
		flexDirection: "column",
		height: "100%",
	},
	mainArea: {
		flex: 1,
		minHeight: 0,
		overflowY: "auto",
	},
	container: {
		padding: tokens.spacingHorizontalM,
	},
	segmented: {
		display: "inline-flex",
		backgroundColor: tokens.colorNeutralBackground3,
		borderRadius: tokens.borderRadiusMedium,
		padding: tokens.spacingHorizontalXXS,
		marginBottom: tokens.spacingVerticalM,
	},
	errorText: {
		display: 'block',
		color: tokens.colorPaletteRedForeground1,
		marginTop: tokens.spacingVerticalS,
		padding: `0 ${tokens.spacingHorizontalM}`,
	},
});

type View = "curriculum" | "account";

function Tool({}: Props) {
	const styles = useStyles();
	const { isAuthenticated, isLoading: authLoading, error, login } = useAuth();
	const { openCheckout } = useSubscription();
	const [view, setView] = React.useState<View>("curriculum");
	const [showTrialPaywall, setShowTrialPaywall] = React.useState(false);

	const { selectedClass, setSelectedClass, classesData, classesLoading, classesError } =
		useClasses();
	const {
		selectedSubject,
		setSelectedSubject,
		subjectsData,
		subjectsLoading,
		subjectsError,
	} = useSubjects(selectedClass);

	const [mode, setMode] = React.useState<"outcomes" | "skills">("outcomes");

	const { strandData, selectedStrand, setSelectedStrand, strandLoading, strandError } =
		useStrandData(selectedClass, selectedSubject, mode === "outcomes");

	const {
		strandUnitsData,
		selectedStrandUnit,
		setSelectedStrandUnit,
		strandUnitsLoading,
		strandUnitsError,
	} = useStrandUnits(selectedClass, selectedSubject, selectedStrand);

	const { skillsAndConceptsData } = useSkillsAndConcepts(selectedClass, selectedSubject);

	const { learningOutcomesData } = useLearningOutcomes(
		selectedClass,
		selectedSubject,
		selectedStrand,
		selectedStrandUnit,
		mode === "outcomes",
	);

	const { isActive: guidanceActive, currentStep, disable: disableGuidance } =
		useOnboardingGuidance({
			selectedClass: selectedClass ?? null,
			selectedSubject: selectedSubject ?? null,
			selectedStrand: selectedStrand ?? null,
			selectedStrandUnit: selectedStrandUnit ?? null,
		});

	return (
		<div className={styles.appRoot}>
			<Header onAccountSettings={() => setView("account")} />

			{guidanceActive && <GuidanceBanner onDisable={disableGuidance} />}

			{/* Trial banner - shows when user is on trial */}
			<TrialBanner onSubscribeClick={() => setShowTrialPaywall(true)} />
			<PaywallModal
				isOpen={showTrialPaywall}
				isAuthenticated={isAuthenticated}
				title="Subscribe"
				onClose={() => setShowTrialPaywall(false)}
				onSubscribe={(tier) => { setShowTrialPaywall(false); openCheckout(tier); }}
				onLogin={() => login('microsoft')}
				onLoginGoogle={() => login('google')}
			/>

			{error && (
				<Text className={styles.errorText}>{error}</Text>
			)}

			{/* Account Settings View */}
			<div className={styles.mainArea}>
			{view === "account" && isAuthenticated ? (
				<AccountSettings onBack={() => setView("curriculum")} />
			) : authLoading ? (
				<Spinner style={{ marginTop: tokens.spacingVerticalXXL }} />
			) : !isAuthenticated ? (
				<PreAuthWelcome />
			) : (
				<div className={styles.container}>
					<Select
						options={classesData}
						label="Select a class"
						value={selectedClass}
						onChange={(newClass) =>
							!Array.isArray(newClass) && setSelectedClass(newClass)
						}
						loading={classesLoading}
						error={classesError}
						copyLabel="class name"
						isHighlighted={currentStep === "CLASS"}
						helperText="Select a class to get started inserting curriculum content into your document."
					/>

					{selectedClass && (
						<Select
							options={subjectsData}
							label="Select a subject"
							value={selectedSubject}
							onChange={(newSubject) =>
								!Array.isArray(newSubject) && setSelectedSubject(newSubject)
							}
							loading={subjectsLoading}
							error={subjectsError}
							copyLabel="subject name"
							isHighlighted={currentStep === "SUBJECT"}
							helperText="Now choose a subject to browse its curriculum content."
						/>
					)}

					{selectedClass && selectedSubject && (
						<div className={styles.segmented}>
							<Button
								appearance={mode === "outcomes" ? "primary" : "subtle"}
								onClick={() => setMode("outcomes")}
							>
								Learning Outcomes
							</Button>
							<Button
								appearance={mode === "skills" ? "primary" : "subtle"}
								onClick={() => setMode("skills")}
							>
								Skills & Concepts
							</Button>
						</div>
					)}

					{mode === "skills" &&
						selectedClass &&
						selectedSubject &&
						skillsAndConceptsData?.data && (
							<SearchAndInsert
								label="Skills & Concepts"
								options={skillsAndConceptsData.data.map((item) => ({
									id: item.id,
									text: cleanText(item.subjectExtra),
								}))}
								placeholder="Search skills and concepts..."
								onInsert={disableGuidance}
							/>
						)}

					{mode === "outcomes" && selectedClass && selectedSubject && (
						<>
							<Select
								options={strandData}
								label="Select a strand"
								value={selectedStrand}
								onChange={(newStrand) =>
									!Array.isArray(newStrand) && setSelectedStrand(newStrand)
								}
								loading={strandLoading}
								error={strandError}
								copyLabel="strand name"
								isHighlighted={currentStep === "STRAND"}
								helperText="Select a strand, then a strand unit — your learning outcomes will appear below."
							/>
							{selectedStrand && (
								<Select
									options={strandUnitsData}
									label="Select a strand unit"
									value={selectedStrandUnit}
									onChange={(newStrandUnit) =>
										!Array.isArray(newStrandUnit) &&
										setSelectedStrandUnit(newStrandUnit)
									}
									loading={strandUnitsLoading}
									error={strandUnitsError}
									copyLabel="strand unit name"
									isHighlighted={currentStep === "STRAND_UNIT"}
									helperText="Now select a strand unit to see the learning outcomes."
								/>
							)}
							{selectedStrandUnit && (
								<>
									{currentStep === "INSERT" && (
										<Text
											style={{
												color: tokens.colorPaletteGreenForeground1,
												fontSize: tokens.fontSizeBase200,
											}}
										>
											Click any outcome below to insert it into your document.
										</Text>
									)}
									<SearchAndInsert
										label="Learning Outcomes"
										options={learningOutcomesData.map((item) => ({
											id: item.key,
											text: cleanText(item.text),
										}))}
										placeholder="Search learning outcomes..."
										onInsert={disableGuidance}
									/>
								</>
							)}
						</>
					)}
				</div>
			)}
			</div>
		</div>
	);
}

export default Tool;
